// Studio team Inc 2 — the per-broadcast agenda/rundown panel. Lives in the studio
// Host Controls area, rendered for host AND cohosts (producers). Internal only —
// never in BroadcastView/viewer. Live-synced via useRoomAgenda; all writes are
// RLS-gated by can_produce_room. Keyed to the room's id (the RLS + realtime key).
// Reorder is drag-and-drop (native HTML5, grip handle) → integer-rewrite.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { GripVertical } from 'lucide-react';
import { useState } from 'react';

import {
  addAgendaItem,
  deleteAgendaItem,
  reorderAgenda,
  updateAgendaItem,
  type AgendaItem,
} from './roomAgendaActions';
import { useRoomAgenda } from './useRoomAgenda';

import { useAuth } from '@/lib/auth/useAuth';
import { cn } from '@/lib/utils/cn';

export function AgendaPanel({ roomId }: { roomId: string }) {
  const { session } = useAuth();
  const userId = session?.user.id ?? '';
  const qc = useQueryClient();
  const agenda = useRoomAgenda(roomId);
  const items = agenda.data ?? [];
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['room-agenda', roomId] });

  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const add = useMutation({
    mutationFn: () => addAgendaItem(roomId, title, userId, note || undefined),
    onSuccess: () => {
      setTitle('');
      setNote('');
      invalidate();
    },
  });
  const reorder = useMutation({
    mutationFn: (ids: string[]) => reorderAgenda(roomId, ids),
    onSuccess: invalidate,
  });

  const handleDrop = (dropIndex: number) => {
    const from = dragIndex;
    setDragIndex(null);
    if (from === null || from === dropIndex) return;
    const ids = items.map((i) => i.id);
    const [moved] = ids.splice(from, 1);
    if (moved === undefined) return;
    ids.splice(dropIndex, 0, moved);
    reorder.mutate(ids);
  };

  return (
    <div className="space-y-3">
      <p className="font-sans text-xs font-medium uppercase tracking-[0.12em] text-video-text-faint">
        Agenda (team only)
      </p>

      {items.length === 0 ? (
        <p className="font-sans text-xs text-video-text-faint">No agenda items yet.</p>
      ) : (
        <ul className="space-y-1">
          {items.map((item, i) => (
            <AgendaRow
              key={item.id}
              item={item}
              roomId={roomId}
              dragging={dragIndex === i}
              dropTarget={dragIndex !== null && dragIndex !== i}
              onDragStart={() => setDragIndex(i)}
              onDragEnd={() => setDragIndex(null)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(i)}
            />
          ))}
        </ul>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (title.trim()) add.mutate();
        }}
        className="space-y-2"
      >
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          placeholder="Agenda item"
          className="w-full rounded-md border border-video-line bg-video-inset px-3 py-2 font-sans text-sm text-video-text outline-none focus:border-video-line-strong"
        />
        <div className="flex gap-2">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            placeholder="Note (optional)"
            className="min-w-0 flex-1 rounded-md border border-video-line bg-video-inset px-3 py-2 font-sans text-xs text-video-text outline-none focus:border-video-line-strong"
          />
          <button
            type="submit"
            disabled={!title.trim() || add.isPending}
            className="rounded-md bg-video-text px-4 py-2 font-sans text-sm font-medium text-video-bg disabled:opacity-50"
          >
            {add.isPending ? 'Adding…' : 'Add'}
          </button>
        </div>
      </form>
      {add.isError && (
        <p className="font-sans text-xs text-oxblood-live">Could not add the item.</p>
      )}
    </div>
  );
}

function AgendaRow({
  item,
  roomId,
  dragging,
  dropTarget,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}: {
  item: AgendaItem;
  roomId: string;
  dragging: boolean;
  dropTarget: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: () => void;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(item.title);
  const [note, setNote] = useState(item.note ?? '');
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['room-agenda', roomId] });
  const toggle = useMutation({
    mutationFn: () => updateAgendaItem(item.id, { covered: !item.covered }),
    onSuccess: invalidate,
  });
  const save = useMutation({
    mutationFn: () => updateAgendaItem(item.id, { title, note }),
    onSuccess: () => {
      setEditing(false);
      invalidate();
    },
  });
  const del = useMutation({
    mutationFn: () => deleteAgendaItem(item.id),
    onSuccess: invalidate,
  });

  const startEdit = () => {
    setTitle(item.title);
    setNote(item.note ?? '');
    setEditing(true);
  };

  return (
    <li
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={cn(
        'flex items-start gap-2 rounded-md border bg-video-inset p-2 transition',
        dragging ? 'border-oxblood-live opacity-50' : 'border-video-line',
        dropTarget && 'border-dashed',
      )}
    >
      <span
        draggable={!editing}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        aria-label="Drag to reorder"
        className="mt-0.5 cursor-grab text-video-text-faint hover:text-video-text active:cursor-grabbing"
      >
        <GripVertical className="h-4 w-4" />
      </span>
      <input
        type="checkbox"
        checked={item.covered}
        onChange={() => toggle.mutate()}
        aria-label="Covered"
        className="mt-1 h-4 w-4 shrink-0 accent-oxblood-live"
      />
      <div className="min-w-0 flex-1">
        {editing ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (title.trim()) save.mutate();
            }}
            className="space-y-1"
          >
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              autoFocus
              placeholder="Title"
              className="w-full rounded border border-video-line bg-video-card px-2 py-1 font-sans text-sm text-video-text outline-none"
            />
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              placeholder="Note (optional)"
              className="w-full rounded border border-video-line bg-video-card px-2 py-1 font-sans text-xs text-video-text outline-none"
            />
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={!title.trim() || save.isPending}
                className="font-sans text-xs text-oxblood-live disabled:opacity-50"
              >
                {save.isPending ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="font-sans text-xs text-video-text-faint"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <>
            <p
              className={cn(
                'font-sans text-sm',
                item.covered ? 'text-video-text-faint line-through' : 'text-video-text',
              )}
            >
              {item.title}
            </p>
            {item.note && <p className="font-sans text-xs text-video-text-faint">{item.note}</p>}
          </>
        )}
      </div>
      {!editing && (
        <div className="flex shrink-0 items-center gap-1 font-sans text-xs text-video-text-faint">
          <button type="button" onClick={startEdit} className="px-1 hover:text-video-text">
            Edit
          </button>
          <button
            type="button"
            onClick={() => del.mutate()}
            className="px-1 hover:text-oxblood-live"
          >
            Delete
          </button>
        </div>
      )}
    </li>
  );
}
